import { LOCALES } from '../../constants/intl';
import {
  DatasetPresetRule,
  TRecordViewMap,
} from '../../models/user-config/DisplayModeConfig';
import {
  DatasetColumnFormat,
  DatasetGridColumn,
} from '../../services/dataset/view-model/DatasetGridColumn';
import UniqueId from '../../services/uid/UniqueId';
import { AppState } from '../reducers/root';

import {
  getDatasetConfigCurrentPreset,
  getDatasetRecordViewMap,
  getDatasetState,
  getDatasetTitle,
} from './dataset';
import { getCurrentLocaleMessages, getCurrentLocaleName } from './intl';
import { getCurrentTheme } from './theme';

type ReviewFormDataResult =
  | {
      isAuto: true;
      data: Record<string, any>;
      template: string;
      title: string;
      titleColor: string;
      titleTextColor: string;
      backgroundColor: string;
      textColor: string;
      width: number;
    }
  | {
      isAuto: false;
      recordViewName: string;
      recordViewMap: TRecordViewMap;
      width: number;
    };

const formatValue = (
  col: DatasetGridColumn,
  value: any,
  locale: string,
  messages: Record<string, string> | undefined
): string => {
  if (value === null || value === undefined) return '';
  switch (col.format) {
    case DatasetColumnFormat.BOOLEAN:
      return value
        ? (messages?.['ui.button.accept'] ?? 'Yes')
        : (messages?.['ui.button.decline'] ?? 'No');
    case DatasetColumnFormat.NUMBER: {
      if (typeof value !== 'number') return String(value);
      if (col.domain?.subtype === 'time') {
        return new Intl.DateTimeFormat(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'UTC',
        }).format(value);
      }
      return new Intl.NumberFormat(locale, {
        useGrouping: col.domain?.subtype !== 'year',
        minimumFractionDigits: col.domain?.precision,
        maximumFractionDigits: col.domain?.precision,
      }).format(value);
    }
    case DatasetColumnFormat.DATE:
      return new Intl.DateTimeFormat(locale).format(new Date(value));
    case DatasetColumnFormat.TIME:
      return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(
        new Date(value)
      );
    case DatasetColumnFormat.DATE_TIME:
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }).format(new Date(value));
    case DatasetColumnFormat.DATE_TIME_SEC:
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      }).format(new Date(value));
    default:
      return String(value).trim();
  }
};

const buildAutoData = (
  columns: DatasetGridColumn[],
  columnsOrder: string[],
  rules: Record<string, DatasetPresetRule>,
  locale: LOCALES,
  messages: Record<string, string> | undefined,
  rowData: Record<string, any>
): Record<string, any> => {
  const orderedVisible = columnsOrder
    .map(field => columns.find(c => c.field === field))
    .filter(
      (col): col is DatasetGridColumn =>
        col !== undefined && (rules[col.field]?.visibility ?? true)
    );

  return {
    qFields: orderedVisible.map(col => ({
      label: col.title,
      value: col.formatter
        ? String(col.formatter(rowData[col.field], { row: rowData }) ?? '')
        : formatValue(col, rowData[col.field], locale, messages),
    })),
  };
};

export const getReviewFormData = (
  state: AppState,
  datasetId: UniqueId
): ReviewFormDataResult => {
  const { recordView } = getDatasetConfigCurrentPreset(state, datasetId);
  const recordViewMap = getDatasetRecordViewMap(state, datasetId) ?? {};
  const recordViewName =
    recordView && Object.keys(recordViewMap).includes(recordView)
      ? recordView
      : Object.keys(recordViewMap)[0];

  const width = recordViewMap[recordViewName]?.width ?? 600;

  if (!recordViewName || !recordViewMap[recordViewName]?.getDataRoutine) {
    const { columns, rows, activeRowId, rowIdField, config } = getDatasetState(
      state,
      datasetId
    );
    const { rules } = getDatasetConfigCurrentPreset(state, datasetId);
    const locale = getCurrentLocaleName(state);
    const messages = getCurrentLocaleMessages(state);
    const activeRow = rows.find(row => row[rowIdField] === activeRowId) ?? {};
    const { colors } = getCurrentTheme(state);

    return {
      isAuto: true,
      data: buildAutoData(
        columns,
        config.columnsOrder,
        rules,
        locale,
        messages,
        activeRow
      ),
      template: recordViewMap[recordViewName].TemplateCode,
      title:
        recordViewMap[recordViewName].label ??
        getDatasetTitle(state, datasetId),
      titleColor: colors.backgroundHeader,
      titleTextColor: colors.typoHeader,
      backgroundColor: colors.surface,
      textColor: colors.typoCommon,
      width,
    };
  }

  return {
    isAuto: false,
    recordViewName,
    recordViewMap,
    width,
  };
};
